import { zip, unzipSync, strToU8, strFromU8, type Zippable } from "fflate";
import { mapDocumentSchema, type MapDocument } from "@51time/domain";
import { api } from "./api";

const MAX_ARCHIVE = 100_000_000;
const PHOTO_PATH = /^photos\/[a-f0-9-]{36}\.jpg$/;
const attachmentIds = (document: MapDocument) => [
  ...new Set(document.features.flatMap((f) => f.attachmentIds)),
];

export async function exportBackup(
  mapId: string,
  progress: (text: string) => void,
) {
  const { map } = await api(`/maps/${mapId}`);
  if (map.role !== "admin")
    throw Error("Only an Admin can back up a private draft.");
  const document = mapDocumentSchema.parse(map.draft);
  const manifest = {
    format: "51time-map-backup",
    version: 1,
    exportedAt: new Date().toISOString(),
    document,
  };
  const files: Zippable = {
    "map.json": strToU8(JSON.stringify(manifest, null, 2)),
    "README.txt": strToU8(
      "WayTogether Maps private map backup\n\nContains the saved Admin draft, private notes/references, labels, views, and every photo attached to that draft. Keep it private.\n\nImport creates a NEW PRIVATE DRAFT. Original sharing links, member access, publication history, unapplied Worker feedback, and basemap imagery are not included. Existing maps are never overwritten.\n",
    ),
  };
  let bytes = JSON.stringify(manifest).length;
  const ids = attachmentIds(document);
  for (let index = 0; index < ids.length; index++) {
    progress(`Saving photo ${index + 1} of ${ids.length}…`);
    const response = await fetch(`/api/photos/${ids[index]}`, {
      credentials: "include",
      cache: "no-store",
      signal: AbortSignal.timeout(90000),
    });
    if (!response.ok)
      throw Error(
        "A photo could not be backed up. No incomplete backup was downloaded.",
      );
    const image = new Uint8Array(await response.arrayBuffer());
    bytes += image.length;
    if (bytes > MAX_ARCHIVE)
      throw Error(
        "This map exceeds the 100 MB portable-backup limit. Split it into smaller maps.",
      );
    files[`photos/${ids[index]}.jpg`] = [image, { level: 0 }];
  }
  const archive = await new Promise<Uint8Array<ArrayBuffer>>(
    (resolve, reject) =>
      zip(files, { level: 6 }, (error, result) =>
        error ? reject(error) : resolve(new Uint8Array(result)),
      ),
  );
  const url = URL.createObjectURL(
    new Blob([archive], { type: "application/zip" }),
  );
  const link = window.document.createElement("a");
  link.href = url;
  link.download = `waytogether-${map.draft.title.replace(/[^a-zA-Z0-9_-]+/g, "-").slice(0, 70)}-${new Date().toISOString().slice(0, 10)}.zip`;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
  progress(
    "Private map backup downloaded. It includes draft photos and private notes.",
  );
}

export function readBackup(bytes: Uint8Array) {
  if (bytes.length > MAX_ARCHIVE)
    throw Error("Choose a map backup smaller than 100 MB.");
  let expanded = 0;
  const names = new Set<string>();
  const files = unzipSync(bytes, {
    filter: (file) => {
      if (names.has(file.name)) throw Error("Backup contains duplicate files.");
      names.add(file.name);
      if (
        file.name !== "map.json" &&
        file.name !== "README.txt" &&
        !PHOTO_PATH.test(file.name)
      )
        throw Error("This is not a supported WayTogether Maps backup.");
      const limit =
        file.name === "map.json"
          ? 3_000_000
          : file.name === "README.txt"
            ? 20000
            : 5_000_000;
      expanded += file.originalSize;
      if (
        file.originalSize > limit ||
        expanded > MAX_ARCHIVE ||
        names.size > 32002
      )
        throw Error("The expanded backup exceeds its size limit.");
      return true;
    },
  });
  if (!files["map.json"])
    throw Error("The map file is missing from this backup.");
  const manifest = JSON.parse(strFromU8(files["map.json"]));
  if (manifest.format !== "51time-map-backup" || manifest.version !== 1)
    throw Error("This backup version is not supported.");
  const document = mapDocumentSchema.parse(manifest.document);
  for (const id of attachmentIds(document)) {
    const bytes = files[`photos/${id}.jpg`];
    if (
      !bytes ||
      bytes.length < 3 ||
      bytes[0] !== 255 ||
      bytes[1] !== 216 ||
      bytes[2] !== 255
    )
      throw Error(
        "A required JPEG photo is missing or damaged. Nothing was imported.",
      );
  }
  return { document, files };
}

export async function importBackup(
  file: File,
  projectId: string,
  progress: (text: string) => void,
) {
  if (file.size > MAX_ARCHIVE)
    throw Error("Choose a backup smaller than 100 MB.");
  const { document, files } = readBackup(
    new Uint8Array(await file.arrayBuffer()),
  );
  const ids = attachmentIds(document),
    replacements = new Map<string, string>();
  for (let index = 0; index < ids.length; index++) {
    progress(`Restoring photo ${index + 1} of ${ids.length}…`);
    const image = files[`photos/${ids[index]}.jpg`];
    let binary = "";
    for (let start = 0; start < image.length; start += 32768)
      binary += String.fromCharCode(...image.subarray(start, start + 32768));
    const result = await api("/photos", "POST", {
      projectId,
      data: btoa(binary),
    });
    replacements.set(ids[index], result.id);
    // Stay inside the service's normal photo-upload rate limit.
    if (index < ids.length - 1)
      await new Promise((resolve) => setTimeout(resolve, 3100));
  }
  const restored = {
    ...document,
    features: document.features.map((f) => ({
      ...f,
      attachmentIds: f.attachmentIds.map((id) => replacements.get(id)!),
    })),
  };
  const { map } = await api(`/projects/${projectId}/import`, "POST", {
    document: restored,
  });
  return map;
}
